import { supabase } from '@/lib/supabase';
import type { AIChatMessage } from '@/types/db';

export type ChatErrorCode =
  | 'unauthorized'
  | 'forbidden'
  | 'profile_not_found'
  | 'invalid_input'
  | 'chat_failed'
  | 'network_error';

export type ChatResult =
  | {
      success: true;
      response: string;
      sourceEventIds: string[];
    }
  | { success: false; error: ChatErrorCode };

interface AskInput {
  profileId: string;
  message: string;
  history: { role: 'user' | 'assistant'; content: string }[];
}

const KNOWN_ERRORS: ReadonlySet<ChatErrorCode> = new Set<ChatErrorCode>([
  'unauthorized',
  'forbidden',
  'profile_not_found',
  'invalid_input',
  'chat_failed',
  'network_error',
]);

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null;
}

function toErrorCode(v: unknown): ChatErrorCode {
  return typeof v === 'string' && KNOWN_ERRORS.has(v as ChatErrorCode)
    ? (v as ChatErrorCode)
    : 'chat_failed';
}

export async function askChat(input: AskInput): Promise<ChatResult> {
  try {
    const { data, error } = await supabase.functions.invoke('chat-with-claude', {
      body: {
        profile_id: input.profileId,
        message: input.message,
        chat_history: input.history,
      },
    });
    if (error) {
      const ctx = (error as { context?: Response }).context;
      let status: number | undefined;
      let bodyText = '';
      if (ctx && typeof ctx.text === 'function') {
        status = ctx.status;
        try {
          bodyText = await ctx.clone().text();
        } catch {
          /* ignore body read failure */
        }
      }
      console.error('[chat] function invoke error', { status, body: bodyText, error });
      if (bodyText) {
        try {
          const parsed = JSON.parse(bodyText) as { error?: unknown };
          if (parsed && typeof parsed.error === 'string' && KNOWN_ERRORS.has(parsed.error as ChatErrorCode)) {
            return { success: false, error: parsed.error as ChatErrorCode };
          }
        } catch {
          /* body not JSON */
        }
      }
      if (status === 401) return { success: false, error: 'unauthorized' };
      if (status === 403) return { success: false, error: 'forbidden' };
      if (status === 400) return { success: false, error: 'invalid_input' };
      if (typeof status === 'number' && status >= 500) {
        return { success: false, error: 'chat_failed' };
      }
      return { success: false, error: 'network_error' };
    }
    if (!isRecord(data)) {
      return { success: false, error: 'chat_failed' };
    }
    if (data.success === true && typeof data.response === 'string') {
      const ids = Array.isArray(data.source_event_ids)
        ? data.source_event_ids.filter((x): x is string => typeof x === 'string')
        : [];
      return { success: true, response: data.response, sourceEventIds: ids };
    }
    return { success: false, error: toErrorCode(data.error) };
  } catch (err) {
    console.error('[chat] unexpected', err);
    return { success: false, error: 'network_error' };
  }
}

export function chatErrorMessage(code: ChatErrorCode): string {
  switch (code) {
    case 'unauthorized':
      return 'Please sign in again.';
    case 'forbidden':
      return "You don't have access to this profile.";
    case 'profile_not_found':
      return "We couldn't find that profile.";
    case 'invalid_input':
      return 'Something about that question confused us. Try again.';
    case 'network_error':
      return "We couldn't reach the server. Check your connection.";
    default:
      return "We couldn't answer that. Please try again.";
  }
}

/** Load the full chat history for this profile, oldest-first. */
export async function listChatMessages(
  profileId: string,
): Promise<AIChatMessage[]> {
  const { data, error } = await supabase
    .from('ai_chat_messages')
    .select('*')
    .eq('profile_id', profileId)
    .order('created_at', { ascending: true });
  if (error) throw error;
  return (data ?? []) as AIChatMessage[];
}
